import type {
  AgentActivityEntry,
  AgentActivityResolvedRisk,
} from "@p0security/integrations-agentic-shared/interface";
import { Flex, Typography } from "@p0security/ui";
import { delegateIconOf } from "components/Agentic/DelegationChain";
import { McpClientIdIcon } from "components/Agentic/clientPlatforms";
import { Identity } from "components/common/Identity";

import type { RequestAttribution } from "../../../Jit/Requests/types.ts";
import { attributionOf } from "../attribution.ts";
import { viewOf } from "../eventProtocols.tsx";
import { RiskLevelBadge } from "./RiskTag.tsx";

/** The catalog name of the risk that decided an IAM-derived level — the
 * first contributing permission scored at that level. Annotation-derived
 * levels carry no catalog risk, so have no name. */
const riskNameOf = ({ explanation, level }: AgentActivityResolvedRisk) =>
  explanation.source === "iam"
    ? explanation.permissions.find(({ score }) => score === level)?.riskName
    : undefined;

/** What the event is "about": its risk when one is named, else the call
 * itself, so the title always leads with something specific. */
const headlineOf = (entry: AgentActivityEntry) =>
  (entry.resolvedRisk && riskNameOf(entry.resolvedRisk)) ??
  viewOf(entry).callName(entry) ??
  entry.method ??
  "Event Details";

/** The identity the event is related to: the delegation chain's subject,
 * else the caller itself when no chain was recorded. */
const subjectOfEntry = ({
  actors,
  caller,
}: AgentActivityEntry): RequestAttribution | undefined =>
  attributionOf(actors, caller) ??
  (caller ? { role: "principal", sub: caller, type: "user" } : undefined);

const IdentityIcon: React.FC<{ type: RequestAttribution["type"] }> = ({
  type,
}) => (
  <span aria-hidden="true" className="inline-flex shrink-0">
    {delegateIconOf({ type: type === "agent" ? "client" : "user" })}
  </span>
);

/** The event drawer's title: its risk (or call), who it is related to,
 * and which client it came through — e.g. "Data exfiltration related to
 * alice@example.com via Alice's Claude agent". */
export const EventTitle: React.FC<{ entry: AgentActivityEntry }> = ({
  entry,
}) => {
  const { clientId, resolvedRisk } = entry;
  const subject = subjectOfEntry(entry);
  // A headless client acts for itself; naming it twice adds nothing.
  const via = clientId && clientId !== subject?.sub ? clientId : undefined;
  return (
    <Flex align="center" gap={8} wrap>
      {resolvedRisk && <RiskLevelBadge level={resolvedRisk.level} />}
      <Typography.Text strong>{headlineOf(entry)}</Typography.Text>
      {subject && (
        <>
          <Typography.Text className="font-normal">related to</Typography.Text>
          <IdentityIcon type={subject.type} />
          <Typography.Text strong>
            <Identity value={subject} />
          </Typography.Text>
        </>
      )}
      {via && (
        <>
          <Typography.Text className="font-normal">via</Typography.Text>
          <McpClientIdIcon clientId={via} />
          <IdentityIcon type="agent" />
          <Typography.Text strong>
            {/* "principal" only so Identity doesn't add its own "via "
                prefix; the connective is rendered above, before the icons.
                A clientId always identifies an agent client. */}
            <Identity value={{ role: "principal", sub: via, type: "agent" }} />
          </Typography.Text>
        </>
      )}
    </Flex>
  );
};
